/**
 * "Er du det korrekte sted?" pop-up.
 * Shown once per browser session. A session cookie (no expiry) is set when the
 * visitor closes the pop-up or submits a language/country, so it doesn't reappear
 * until the browser is closed.
 *
 * The markets use separate domains (.dk, .de, ...), and cookies don't cross domains.
 * So on submit a marker param is added to the redirect URL; the target domain sees it,
 * sets its own cookie and removes the param again.
 */
if (!customElements.get('geo-popup')) {
  customElements.define(
    'geo-popup',
    class GeoPopup extends HTMLElement {
      static COOKIE_NAME = 'geo_popup_seen';
      static URL_PARAM = 'geo_popup';

      constructor() {
        super();
        this.dialog = this.querySelector('[role="dialog"]');
        this.form = this.querySelector('form');
        this.onKeyUp = this.onKeyUp.bind(this);
      }

      connectedCallback() {
        // Don't get in the way while editing the theme
        if (window.Shopify && window.Shopify.designMode) return;
        if (GeoPopup.cameFromPopupOnOtherDomain()) GeoPopup.rememberChoice();
        if (GeoPopup.hasSeenPopup()) return;

        this.querySelectorAll('[data-geo-popup-close]').forEach((element) =>
          element.addEventListener('click', () => this.close())
        );
        // Let the form submit normally: Shopify redirects to the chosen market
        this.form.addEventListener('submit', () => {
          GeoPopup.rememberChoice();
          this.addMarkerToReturnUrl();
        });

        setTimeout(() => this.open(), 500);
      }

      disconnectedCallback() {
        document.removeEventListener('keyup', this.onKeyUp);
      }

      open() {
        this.removeAttribute('hidden');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keyup', this.onKeyUp);

        const firstField = this.dialog.querySelector('select, button[type="submit"]');
        if (typeof trapFocus === 'function') {
          trapFocus(this.dialog, firstField);
        } else if (firstField) {
          firstField.focus();
        }
      }

      close() {
        GeoPopup.rememberChoice();
        this.setAttribute('hidden', '');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keyup', this.onKeyUp);
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }

      /**
       * Shopify redirects to return_to (a path) on the chosen market's domain,
       * so the marker param travels with the visitor to the new domain.
       */
      addMarkerToReturnUrl() {
        let returnTo = this.form.querySelector('input[name="return_to"]');
        if (!returnTo) {
          returnTo = document.createElement('input');
          returnTo.type = 'hidden';
          returnTo.name = 'return_to';
          returnTo.value = window.location.pathname + window.location.search;
          this.form.appendChild(returnTo);
        }

        const url = new URL(returnTo.value || '/', window.location.origin);
        url.searchParams.set(GeoPopup.URL_PARAM, 'seen');
        returnTo.value = url.pathname + url.search;
      }

      onKeyUp(event) {
        if (event.code === 'Escape') this.close();
      }

      static hasSeenPopup() {
        return document.cookie.split('; ').some((cookie) => cookie.startsWith(`${GeoPopup.COOKIE_NAME}=`));
      }

      /**
       * True when the visitor was just sent here from the pop-up on another domain.
       * Removes the marker param from the address bar so it isn't shared or bookmarked.
       */
      static cameFromPopupOnOtherDomain() {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(GeoPopup.URL_PARAM)) return false;

        url.searchParams.delete(GeoPopup.URL_PARAM);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        return true;
      }

      static rememberChoice() {
        // No expires/max-age = session cookie
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${GeoPopup.COOKIE_NAME}=1; path=/; SameSite=Lax${secure}`;
      }
    }
  );
}
